<template>
    <div class="login-container" v-if="!success && !invalidLink">
        <div class="login-card">
            <!-- Header -->
            <div class="login-header">
                <div class="logo">
                    <div class="logo-icon">🏣</div>
                    <h1>Reset Password</h1>
                </div>
                <p class="subtitle">Create new password</p>
            </div>

            <!-- Reset Form -->
            <form @submit.prevent="handleReset" class="login-form">
                <div class="form-group">
                    <label for="password">Password</label>
                    <div class="input-container">
                        <span class="input-icon">🔒</span>
                        <input :type="showPassword ? 'text' : 'password'" id="password" v-model="form.password"
                            placeholder="Enter your password" required />
                        <button type="button" class="password-toggle" @click="showPassword = !showPassword">
                            {{ showPassword ? "👁️" : "👁️‍🗨️" }}
                        </button>
                    </div>
                </div>
                <div class="form-group">
                    <label for="confirmpassword">Confirm Password</label>
                    <div class="input-container">
                        <span class="input-icon">🔒</span>
                        <input :type="showConfirmPassword ? 'text' : 'password'" id="confirmpassword"
                            v-model="form.confirmpassword" placeholder="Confirm your password" required />
                        <button type="button" class="password-toggle" @click="showConfirmPassword = !showConfirmPassword">
                            {{ showConfirmPassword ? "👁️" : "👁️‍🗨️" }}
                        </button>
                    </div>
                </div>

                <!-- Password requirements -->
                <div class="password-requirements" v-if="form.password">
                    <p class="requirements-title">Password must contain:</p>
                    <ul>
                        <li :class="{ 'valid': hasMinLength }">At least 8 characters</li>
                        <li :class="{ 'valid': hasUpperCase }">One uppercase letter</li>
                        <li :class="{ 'valid': hasLowerCase }">One lowercase letter</li>
                        <li :class="{ 'valid': hasNumber }">One number</li>
                        <li :class="{ 'valid': hasSpecialChar }">One special character</li>
                    </ul>
                </div>

                <div class="form-group">
                    <p v-if="errmsg !== null" class="text-danger">{{ errmsg }}</p>
                    <p v-if="!passwordsMatch && form.confirmpassword" class="text-danger">Passwords do not match</p>
                </div>
                <button type="submit" class="login-btn" :disabled="loading || !isFormValid">
                    <span v-if="loading" class="spinner"></span>
                    <span v-else>Reset Password</span>
                </button>
            </form>

            <!-- Footer -->
            <div class="login-footer">
                <p>Remember your password? <router-link to="/login" class="signup-link">Sign in</router-link></p>
            </div>
        </div>

        <!-- Background Elements -->
        <div class="floating-element element-1"></div>
        <div class="floating-element element-2"></div>
        <div class="floating-element element-3"></div>
    </div>

    <!-- Success Confirmation -->
    <div class="mail-sent-container" v-else-if="success">
        <div class="success-card">
            <!-- Success Icon -->
            <div class="success-icon">
                <div class="icon-circle">
                    <span class="check-icon">✓</span>
                </div>
            </div>

            <!-- Success Message -->
            <div class="success-message">
                <h1>Password Reset Successful!</h1>
                <p class="subtitle">Your password has been updated successfully</p>
                <p class="instructions">
                    You can now log in with your new password. For security reasons, we recommend using a unique password that you don't use elsewhere.
                </p>
            </div>

            <!-- Action Buttons -->
            <div class="action-buttons">
                <button class="login-redirect-btn" @click="redirectToLogin">
                    Go to Login
                </button>
            </div>

            <!-- Security Tip -->
            <div class="help-text">
                <p><strong>Security Tip:</strong> Consider enabling two-factor authentication for added security.</p>
            </div>
        </div>

        <!-- Background Elements -->
        <div class="floating-element element-1"></div>
        <div class="floating-element element-2"></div>
        <div class="floating-element element-3"></div>
    </div>

    <!-- Invalid Link Message -->
    <div class="mail-sent-container" v-else-if="invalidLink">
        <div class="success-card error-card">
            <!-- Error Icon -->
            <div class="success-icon">
                <div class="icon-circle error-circle">
                    <span class="error-icon">⚠️</span>
                </div>
            </div>

            <!-- Error Message -->
            <div class="success-message">
                <h1>Invalid or Expired Link</h1>
                <p class="subtitle">This password reset link is no longer valid</p>
                <p class="instructions">
                    The password reset link may have expired or already been used. Password reset links are valid for 24 hours only.
                </p>
            </div>

            <!-- Action Buttons -->
            <div class="action-buttons">
                <button class="resend-btn" @click="redirectToForgotPassword">
                    Request New Link
                </button>
                <button class="back-btn" @click="redirectToLogin">
                    Back to Login
                </button>
            </div>

            <!-- Help Text -->
            <div class="help-text">
                <p>Need help? Contact our <a href="mailto:support@example.com" class="resend-link">support team</a></p>
            </div>
        </div>

        <!-- Background Elements -->
        <div class="floating-element element-1"></div>
        <div class="floating-element element-2"></div>
        <div class="floating-element element-3"></div>
    </div>
</template>

<script setup>
import router from "@/router";
import authService from "@/services/auth.service";
import toastService from "@/services/toastService";
import { ref, reactive, computed, onMounted } from "vue";
import { useRoute } from "vue-router";

const route = useRoute();
const hash = ref(null);
const success = ref(false);
const invalidLink = ref(false);
const showPassword = ref(false);
const showConfirmPassword = ref(false);
const loading = ref(false);
const errmsg = ref(null);

const form = reactive({
    password: "",
    confirmpassword: "",
});

// Password validation
const hasMinLength = computed(() => form.password.length >= 8);
const hasUpperCase = computed(() => /[A-Z]/.test(form.password));
const hasLowerCase = computed(() => /[a-z]/.test(form.password));
const hasNumber = computed(() => /[0-9]/.test(form.password));
const hasSpecialChar = computed(() => /[!@#$%^&*(),.?":{}|<>]/.test(form.password));
const passwordsMatch = computed(() => form.password === form.confirmpassword);
const isPasswordValid = computed(() => 
    hasMinLength.value && 
    hasUpperCase.value && 
    hasLowerCase.value && 
    hasNumber.value && 
    hasSpecialChar.value
);
const isFormValid = computed(() => 
    isPasswordValid.value && 
    passwordsMatch.value && 
    form.password.length > 0
);

onMounted(() => {
    // Get the hash parameter from URL
    const urlHash = route.params.key || route.query.key;
    
    if (!urlHash) {
        // Redirect to home if no hash parameter
        invalidLink.value = true;
        toastService.error("Invalid password reset link");
        setTimeout(() => {
            router.push("/");
        }, 3000);
    } else {
        hash.value = urlHash;
        // Optionally validate the hash with backend
        validateHash(urlHash);
    }
});

const validateHash = async (hashValue) => {
    hashValue;
    try {
        loading.value = true;
        // Call API to validate the reset hash
        // const resp = await authService.validateResetHash({ hash: hashValue });
        // if (resp.error) {
        //     invalidLink.value = true;
        //     toastService.error(resp.error.message || "Invalid reset link");
        // }
    } catch (error) {
        invalidLink.value = true;
        toastService.error("Failed to validate reset link");
    } finally {
        loading.value = false;
    }
};

const handleReset = async () => {
    if (!isFormValid.value) {
        errmsg.value = "Please fix the validation errors above";
        return;
    }
    
    errmsg.value = null;
    loading.value = true;
    
    try {
        const resp = await authService.resetPassword({ 
            hash: hash.value, 
            password: form.password, 
            confirmpassword: form.confirmpassword 
        });
        
        if (!resp.error) {
            success.value = true;
            toastService.success("Password reset successfully!");
            // Auto-redirect to login after 5 seconds
            setTimeout(() => {
                router.push("/login");
            }, 10000);
        } else {
            errmsg.value = resp.error ? resp.error.message : "Server is unreachable";
            
            // Check if the error is due to invalid/expired hash
            if (resp.error?.code === 'INVALID_HASH' || resp.error?.message?.toLowerCase().includes('invalid') || resp.error?.message?.toLowerCase().includes('expired')) {
                invalidLink.value = true;
            }
        }
    } catch (error) {
        errmsg.value = error.error ? error.error.message : "Server is unreachable";
        toastService.error(errmsg.value);
    } finally {
        loading.value = false;
    }
};

const redirectToLogin = () => {
    router.push("/login");
};

const redirectToForgotPassword = () => {
    router.push("/forgot-password");
};
</script>

<style scoped>
.login-container {
    min-height: 100vh;
    display: flex;
    align-items: center;
    justify-content: center;
    background: linear-gradient(135deg, #ea6666 0%, #a26e4b 100%);
    padding: 20px;
    position: relative;
    overflow: hidden;
}

.login-card {
    background: white;
    padding: 40px;
    border-radius: 20px;
    box-shadow: 0 20px 40px rgba(0, 0, 0, 0.1);
    width: 100%;
    max-width: 420px;
    position: relative;
    z-index: 2;
    backdrop-filter: blur(10px);
}

.login-header {
    text-align: center;
    margin-bottom: 30px;
}

.logo {
    display: flex;
    align-items: center;
    justify-content: center;
    gap: 12px;
    margin-bottom: 8px;
}

.logo-icon {
    font-size: 2rem;
}

h1 {
    color: #2d3748;
    font-size: 1.8rem;
    font-weight: 700;
    margin: 0;
}

.subtitle {
    color: #718096;
    margin: 0;
    font-size: 0.95rem;
}

.login-form {
    margin-bottom: 25px;
}

.form-group {
    margin-bottom: 20px;
}

label {
    display: block;
    margin-bottom: 8px;
    color: #2d3748;
    font-weight: 500;
    font-size: 0.9rem;
}

.input-container {
    position: relative;
    display: flex;
    align-items: center;
}

.input-icon {
    position: absolute;
    left: 15px;
    font-size: 1.1rem;
    z-index: 1;
}

input {
    width: 100%;
    padding: 12px 45px 12px 45px;
    border: 2px solid #e2e8f0;
    border-radius: 12px;
    font-size: 1rem;
    transition: all 0.3s ease;
    background: #f8fafc;
}

input:focus {
    outline: none;
    border-color: #b6300f;
    background: white;
    box-shadow: 0 0 0 3px rgba(182, 48, 15, 0.1);
}

.password-toggle {
    position: absolute;
    right: 15px;
    background: none;
    border: none;
    font-size: 1.1rem;
    cursor: pointer;
    padding: 0;
}

.form-options {
    display: flex;
    justify-content: space-between;
    align-items: center;
    margin-bottom: 25px;
}

.checkbox-container {
    display: flex;
    align-items: center;
    cursor: pointer;
    font-size: 0.9rem;
    color: #4a5568;
}

.checkbox-container input {
    display: none;
}

.checkmark {
    width: 18px;
    height: 18px;
    border: 2px solid #cbd5e0;
    border-radius: 4px;
    margin-right: 8px;
    position: relative;
    transition: all 0.3s ease;
}

.checkbox-container input:checked+.checkmark {
    background: #b6300f;
    border-color: #b6300f;
}

.checkbox-container input:checked+.checkmark::after {
    content: "✓";
    color: white;
    position: absolute;
    top: 50%;
    left: 50%;
    transform: translate(-50%, -50%);
    font-size: 12px;
}

.forgot-password {
    color: #b6300f;
    text-decoration: none;
    font-size: 0.9rem;
    font-weight: 500;
}

.forgot-password:hover {
    text-decoration: underline;
}

.login-btn {
    width: 100%;
    padding: 14px;
    background: linear-gradient(135deg, #b6300f, #d93814);
    color: white;
    border: none;
    border-radius: 12px;
    font-size: 1rem;
    font-weight: 600;
    cursor: pointer;
    transition: all 0.3s ease;
    display: flex;
    align-items: center;
    justify-content: center;
    gap: 10px;
}

.login-btn:hover:not(:disabled) {
    transform: translateY(-2px);
    box-shadow: 0 8px 20px rgba(182, 48, 15, 0.3);
}

.login-btn:disabled {
    opacity: 0.7;
    cursor: not-allowed;
}

.spinner {
    width: 18px;
    height: 18px;
    border: 2px solid transparent;
    border-top: 2px solid white;
    border-radius: 50%;
    animation: spin 1s linear infinite;
}

@keyframes spin {
    0% {
        transform: rotate(0deg);
    }

    100% {
        transform: rotate(360deg);
    }
}

.divider {
    text-align: center;
    margin: 25px 0;
    position: relative;
}

.divider::before {
    content: "";
    position: absolute;
    top: 50%;
    left: 0;
    right: 0;
    height: 1px;
    background: #e2e8f0;
}

.divider span {
    background: white;
    padding: 0 15px;
    color: #718096;
    font-size: 0.9rem;
}

.social-login {
    display: grid;
    grid-template-columns: 1fr 1fr;
    gap: 12px;
    margin-bottom: 25px;
}

.social-btn {
    display: flex;
    align-items: center;
    justify-content: center;
    gap: 8px;
    padding: 12px;
    border: 2px solid #e2e8f0;
    border-radius: 12px;
    background: white;
    cursor: pointer;
    transition: all 0.3s ease;
    font-weight: 500;
}

.social-btn:hover {
    border-color: #b6300f;
    transform: translateY(-1px);
}

.login-footer {
    text-align: center;
}

.login-footer p {
    color: #718096;
    margin: 0;
    font-size: 0.9rem;
}

.signup-link {
    color: #b6300f;
    text-decoration: none;
    font-weight: 600;
}

.signup-link:hover {
    text-decoration: underline;
}

.floating-element {
    position: absolute;
    border-radius: 50%;
    background: linear-gradient(135deg, rgba(182, 48, 15, 0.1), rgba(106, 176, 76, 0.1));
    animation: float 6s ease-in-out infinite;
}

.element-1 {
    width: 100px;
    height: 100px;
    top: 10%;
    left: 10%;
    animation-delay: 0s;
}

.element-2 {
    width: 150px;
    height: 150px;
    bottom: 20%;
    right: 10%;
    animation-delay: 2s;
}

.element-3 {
    width: 80px;
    height: 80px;
    top: 60%;
    left: 5%;
    animation-delay: 4s;
}

@keyframes float {

    0%,
    100% {
        transform: translateY(0px) rotate(0deg);
    }

    50% {
        transform: translateY(-20px) rotate(180deg);
    }
}

@media (max-width: 480px) {
    .login-card {
        padding: 30px 20px;
    }

    .social-login {
        grid-template-columns: 1fr;
    }
}

.password-requirements {
    background: #f8fafc;
    border-radius: 8px;
    padding: 12px 16px;
    margin-bottom: 20px;
    border-left: 4px solid #b6300f;
}

.requirements-title {
    color: #2d3748;
    font-weight: 600;
    margin-bottom: 8px;
    font-size: 0.9rem;
}

.password-requirements ul {
    list-style: none;
    padding: 0;
    margin: 0;
}

.password-requirements li {
    color: #718096;
    font-size: 0.85rem;
    margin-bottom: 4px;
    padding-left: 20px;
    position: relative;
}

.password-requirements li:before {
    content: "✗";
    position: absolute;
    left: 0;
    color: #e53e3e;
}

.password-requirements li.valid:before {
    content: "✓";
    color: #38a169;
}

.password-requirements li.valid {
    color: #38a169;
}

.text-danger {
    color: #e53e3e;
    font-size: 0.9rem;
    margin: 5px 0;
}

/* Success/Error container styles (similar to previous template) */
.mail-sent-container {
    min-height: 100vh;
    display: flex;
    align-items: center;
    justify-content: center;
    background: linear-gradient(135deg, #ea6666 0%, #a26e4b 100%);
    padding: 20px;
    position: relative;
    overflow: hidden;
}

.success-card {
    background: white;
    padding: 50px 40px;
    border-radius: 20px;
    box-shadow: 0 20px 40px rgba(0, 0, 0, 0.1);
    width: 100%;
    max-width: 480px;
    position: relative;
    z-index: 2;
    text-align: center;
}

.error-card {
    animation: shake 0.5s ease-in-out;
}

.success-icon {
    margin-bottom: 30px;
}

.icon-circle {
    width: 100px;
    height: 100px;
    background: linear-gradient(135deg, #4CAF50, #45a049);
    border-radius: 50%;
    margin: 0 auto;
    display: flex;
    align-items: center;
    justify-content: center;
    position: relative;
    animation: scaleIn 0.5s ease-out;
}

.error-circle {
    background: linear-gradient(135deg, #f56565, #e53e3e);
}

.check-icon, .error-icon {
    font-size: 3rem;
    color: white;
    font-weight: bold;
}

.success-message h1 {
    color: #2d3748;
    font-size: 2rem;
    font-weight: 700;
    margin-bottom: 10px;
}

.success-message .subtitle {
    color: #718096;
    margin-bottom: 5px;
    font-size: 1rem;
}

.instructions {
    color: #4a5568;
    line-height: 1.6;
    margin: 20px 0;
    font-size: 0.95rem;
}

.action-buttons {
    display: flex;
    flex-direction: column;
    gap: 15px;
    margin: 30px 0;
}

.login-redirect-btn, .resend-btn, .back-btn {
    padding: 14px;
    border-radius: 12px;
    font-size: 1rem;
    font-weight: 600;
    cursor: pointer;
    transition: all 0.3s ease;
    border: none;
}

.login-redirect-btn, .resend-btn {
    background: linear-gradient(135deg, #b6300f, #d93814);
    color: white;
}

.login-redirect-btn:hover:not(:disabled),
.resend-btn:hover:not(:disabled) {
    transform: translateY(-2px);
    box-shadow: 0 8px 20px rgba(182, 48, 15, 0.3);
}

.back-btn {
    background: #f8fafc;
    color: #4a5568;
    border: 2px solid #e2e8f0;
}

.back-btn:hover {
    background: #edf2f7;
    border-color: #cbd5e0;
    transform: translateY(-1px);
}

.help-text {
    margin-top: 20px;
    padding-top: 20px;
    border-top: 1px solid #e2e8f0;
}

.help-text p {
    color: #718096;
    font-size: 0.9rem;
    margin: 0;
}

.resend-link {
    color: #b6300f;
    text-decoration: none;
    font-weight: 500;
}

.resend-link:hover {
    text-decoration: underline;
}

/* Animations */
@keyframes scaleIn {
    0% {
        transform: scale(0);
        opacity: 0;
    }
    70% {
        transform: scale(1.1);
    }
    100% {
        transform: scale(1);
        opacity: 1;
    }
}

@keyframes shake {
    0%, 100% { transform: translateX(0); }
    25% { transform: translateX(-5px); }
    75% { transform: translateX(5px); }
}

/* Floating elements (same as before) */
.floating-element {
    position: absolute;
    border-radius: 50%;
    background: linear-gradient(135deg, rgba(182, 48, 15, 0.1), rgba(106, 176, 76, 0.1));
    animation: float 6s ease-in-out infinite;
}

.element-1 {
    width: 100px;
    height: 100px;
    top: 10%;
    left: 10%;
    animation-delay: 0s;
}

.element-2 {
    width: 150px;
    height: 150px;
    bottom: 20%;
    right: 10%;
    animation-delay: 2s;
}

.element-3 {
    width: 80px;
    height: 80px;
    top: 60%;
    left: 5%;
    animation-delay: 4s;
}

@keyframes float {
    0%, 100% {
        transform: translateY(0px) rotate(0deg);
    }
    50% {
        transform: translateY(-20px) rotate(180deg);
    }
}

@media (max-width: 480px) {
    .success-card {
        padding: 40px 20px;
    }
    
    .icon-circle {
        width: 80px;
        height: 80px;
    }
    
    .check-icon, .error-icon {
        font-size: 2.5rem;
    }
    
    .success-message h1 {
        font-size: 1.6rem;
    }
    
    .action-buttons {
        flex-direction: column;
    }
}
</style>